import React, { useState, useEffect, useRef } from 'react';
import axiosInstance, { getApiBaseUrl } from './../components/services/axiosInstance.js';
import ProductGrid from '../components/ProductGrid.jsx';
import { useTranslation } from 'react-i18next';
import TopBar from './Navigation/TopBar';
import BottomBar from './Navigation/BottomBar';
import { Box, Button, Autocomplete, TextField } from '@mui/material';
import io from 'socket.io-client';
import { useAuth } from './services/AuthContext';
import { useNavigate } from 'react-router-dom';
// Top of the file
import { useSEO } from './SEOContext';
import HeroSection from './pages/HomePage/HeroSection';
import axios from 'axios';
import BuyerHeroSection from './pages/HomePage/BuyerHeroSection';
import SellerHeroSection from './pages/HomePage/SellerHeroSection';
import CheckpointHeroSection from './pages/HomePage/CheckpointHeroSection';
import HowItWorksSection from './pages/HomePage/HowItWorksSection';
import ValuePropositionGrid from './pages/HomePage/ValuePropositionGrid';
import PilotCallout from './pages/HomePage/PilotCallout';
import FutureVisionTeaser from './pages/HomePage/FutureVisionTeaser';
import Footer from './pages/HomePage/Footer';


const MainScrollingPage = () => {
  const { t, i18n } = useTranslation();
  const { getUserUUID, isAuthenticated } = useAuth(); // Assuming isAuthenticated is a function
  const navigate = useNavigate();
  const { setSeoTags } = useSEO(); // Access the function to set SEO tags
  const [products, setProducts] = useState([]);
  const [markedProducts, setMarkedProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    selectedCategories: [],
  });
  const pageSize = 40;


 
  // Fetch categories on component mount
 // Fetch categories whenever the language changes
 // Fetch categories on component mount
// Update the categories fetching useEffect
useEffect(() => {
  console.log('Fetching categories...');
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await axiosInstance.get('/api/categories/', {
                params: { langCode: i18n.language },
      });
      console.log('Categories fetched:', response.data);
      // Use main_categories instead of subcategories
      setCategories(response.data.main_categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  fetchCategories();
}, [i18n.language]);
/////////////////////
useEffect(() => {
  // Setting dynamic SEO tags for this page with translations
  setSeoTags({
    title: t('seo.home.title'), // Translation key for the title
    description: t('seo.home.description'), // Translation key for the description
    keywords: t('seo.home.keywords') // Translation key for keywords
  });
}, [setSeoTags, t]); // Ensure the effect re-runs when translation function or setSeoTags changes
  // Reset products when language or category filter changes
// Reset products and filters when language changes
useEffect(() => {
  const languageChangedHandler = () => {
    setFilters({ selectedCategories: [] }); // reset chips
    setPage(1);
  };

  i18n.on('languageChanged', languageChangedHandler);
  return () => i18n.off('languageChanged', languageChangedHandler);
}, [i18n]);

// Refetch products when filters, page, or language change
useEffect(() => {
  fetchData();
}, [page, filters.selectedCategories, i18n.language]);


// Filter chip change handler
const handleFilterChange = (event, newValue) => {
  setFilters(prev => ({
    ...prev,
    selectedCategories: newValue
  }));
  setPage(1); // Reset to first page when filters change
};



  ////////////////////////////////////////////////////////////////////

  // Fetch products based on filters and page number
  // Fetch products from API
  // Update the fetchData function to use main categories directly
const fetchData = async () => {
  setLoading(true);
  try {
    // Use the category values directly since they're now main categories
    const selectedCategoryValues = filters.selectedCategories.map(category => category.value);
    
const response = await axiosInstance.get('/api/datastream/', {
      params: {
        lang_code: i18n.language,
        page,
        pageSize,
        categories: selectedCategoryValues.length > 0 
          ? selectedCategoryValues.join(',') 
          : undefined,
      },
    });
        console.log("API response data:", response.data);  // Add this debug line


    if (response.data && Array.isArray(response.data.data)) {
      const newProducts = response.data.data.map(product => ({
        id: product.id,
        product_name: product.product_name,
        product_description: product.product_description,
        price_gross: product.price_gross,
        currency: product.currency,
        image_url1: product.image_url1,
        product_main_category: product.product_main_category,
        product_sub_category: product.product_sub_category,
      }));

      if (page === 1) {
        setProducts(newProducts);
      } else {
        setProducts(prevProducts => [...prevProducts, ...newProducts]);
      }

      const pagination = response.data.pagination || {};
      setShowLoadMore(newProducts.length === pageSize && pagination?.total_pages > page);

    } else {
      console.error('Unexpected API response format:', response.data);
      setError(t('api_error'));
    }
  } catch (error) {
    console.error('Error fetching data from the API:', error);
    setError(t('api_error'));
  } finally {
    setLoading(false);
  }
};
  
const socketRef = useRef(null); // To persist socket instance across re-renders

  // Initialize socket connection only if user is logged in
// Initialize socket connection only if user is logged in
useEffect(() => {
  if (!isAuthenticated()) return;

  // Prevent multiple socket instances
  if (!socketRef.current) {
    socketRef.current = io(import.meta.env.VITE_SOCKET_URL, {
      transports: ['websocket'],
      withCredentials: true,
    });
  }

  const socket = socketRef.current;

  socket.on('connect', () => {
    console.log('Connected to server');
  });

  socket.on('disconnect', () => {
    console.log('Disconnected from server');
  });

  socket.on('product_marked', (productId) => {
    setMarkedProducts(prev => [...prev, { product_id: productId }]);
  });

  socket.on('product_unmarked', (productId) => {
    setMarkedProducts(prev => prev.filter(item => item.product_id !== productId));
  });

  return () => {
    socket.off('connect');
    socket.off('disconnect');
    socket.off('product_marked');
    socket.off('product_unmarked');
    socket.disconnect();
    socketRef.current = null;
  };
}, [isAuthenticated]);


  // Fetch marked products if user is logged in 

  useEffect(() => {
    // Define an async function inside useEffect
    const fetchMarkedProducts = async () => {
      try {
        if (isAuthenticated()) {
          const userUuid = getUserUUID(); // Assuming this function is defined elsewhere
          if (userUuid) {
            const response = await axiosInstance.get('/api/marked-products/', {
              params: { userUid: userUuid },
            });
            console.log("Marked Products:", response.data);
            setMarkedProducts(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching marked products:", error.response?.data || error);
      }
    };

    fetchMarkedProducts(); // Call the async function inside useEffect
  }, [getUserUUID, isAuthenticated]); // Dependency array for useEffect



  const truncateDescription = (description, maxLength = 100) => {
    if (description.length > maxLength) {
      return description.slice(0, maxLength) + '...';
    }
    return description;
  };

  // Function to load more products
  const loadMoreProducts = () => {
    setPage(prevPage => prevPage + 1);
  };

  // Function to mark/unmark a product
const toggleMarkProduct = async (productId) => {
  try {
    // Get the user UUID (throws an error if not logged in)
    const userId = await getUserUUID(); 
    
    // If the user is not logged in, show a simple confirmation popup
    if (!userId) {
      const loginConfirmed = window.confirm('You need to be logged in to perform this action. Do you want to log in?');
      if (!loginConfirmed) return; // If the user doesn't confirm, stop the action
      window.location.href = '/signin'; // Redirect to login page
      return;
    }

    const productIndex = markedProducts.findIndex(item => item.product_id === productId);
    if (productIndex !== -1) {
      // Unmark product if already marked
      const updatedMarkedProducts = [...markedProducts];
      updatedMarkedProducts.splice(productIndex, 1);
      setMarkedProducts(updatedMarkedProducts);
      await unmarkProduct(productId);
    } else {
      // Mark the product if not already marked
      const product = products.find(item => item.id === productId);
      if (product) {
        setMarkedProducts(prevMarkedProducts => [...prevMarkedProducts, { product_id: productId }]);
        await markProduct(productId);
      }
    }
  } catch (error) {
    // If an error occurs in getting the user ID or during the marking/unmarking process
    console.error('Error toggling mark:', error);
    alert(t('You need to be logged in to perform this action.'));
  }
};

  

 // Function to mark a product
const markProduct = async (productId) => {
  const userUid = getUserUUID();  // Assuming this function gets the user UUID

  if (!userUid) {
    console.error("User UUID is missing");
    return;
  }

  try {
    // Call to the API to mark the product
    const response = await axiosInstance.post('/api/mark-product', 
      { productId, userUid }, 
      { headers: { 'Content-Type': 'application/json' } }
    );

    console.log("Success:", response.data);  // Log success message
  } catch (error) {
    // Log error response or error message
    console.error("Error:", error.response ? error.response.data : error);
  }
};

  // Function to unmark a product
  const unmarkProduct = async (productId) => {
    try {
      const userUid = getUserUUID(); // Ensure we get the user UUID
      if (!userUid) {
        console.error("User UUID is missing");
        return;
      }
  
      const response = await axiosInstance.delete("/api/unmark_product", {
        data: {
          productId,
          userUid,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });
  
    } catch (error) {
      console.error("Error unmarking product:", error);
    }
  };
  

////////






 


  //////////////
  
  return (
    <Box sx={{ paddingTop: '-130px', paddingBottom: '-10px' }}>
      <HeroSection />
      <BuyerHeroSection />
      <SellerHeroSection />
      <CheckpointHeroSection />
      <HowItWorksSection />
      <ValuePropositionGrid />
      <PilotCallout />
      <FutureVisionTeaser />

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
      <Autocomplete
  multiple
  id="category-autocomplete"
  options={categories}
  getOptionLabel={(option) => option.label || ''}
  value={filters.selectedCategories}
  onChange={handleFilterChange} // Pass the event and newValue directly
  isOptionEqualToValue={(option, value) => option.value === value.value}
  renderInput={(params) => (
    <TextField
      {...params}
      variant="standard"
      label={t('select_category')}
      placeholder={t('all_categories')}
    />
  )}
  loading={categoriesLoading}
  disabled={categoriesLoading}
  sx={{ minWidth: '250px', mr: 2 }}
/>

      </Box>

      <div style={{ paddingBottom: '64px' }}>
  <ProductGrid
    products={products}
    markedProducts={markedProducts}
    toggleMarkProduct={toggleMarkProduct}
    truncateDescription={truncateDescription}
  />

  {loading && <p style={{ textAlign: 'center' }}>{t('loading')}</p>}
  {!loading && error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}

  {showLoadMore && !loading && (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 4,
      }}
    >
      <Button
        onClick={loadMoreProducts}
        variant="contained"
        sx={{
          backgroundColor: 'var(--electric-pink)',
          color: 'white',
          padding: '12px 24px',
          fontWeight: 'bold',
          textTransform: 'none',
          borderRadius: '8px',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: 'var(--fiery-rose)',
            boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.15)',
          },
          '&:active': {
            backgroundColor: 'var(--electric-pink)',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {t('load_more')}
      </Button>
    </Box>
  )}
 
      </div>

      <Footer />
    </Box>
  );
};

export default MainScrollingPage;
